import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const to = new Date();
// Set a more reasonable default date range - last 12 months instead of from 2000
const from = new Date(to);
from.setFullYear(from.getFullYear() - 1);

const useAppStore = create(
  persist(
    set => ({ // Removed unused 'get' parameter
      // Initial state - exactly matching your Pullstate store
      authentication: {},
      kyc: null,
      hubspot: {},
      selectedAccount: null,
      allSyncsAvailable: null,
      allOpportunitiesAvailable: null,
      selectedCatalogue: null,
      tenantCurrencyCode: "",
      tenantCurrencySymbol: "*",
      tenantCurrencyLocale: "",
      serverError: null,
      onboarding: {
        howHear: null,
        role: null,
        accountName: null,
        address1: null,
        address2: null,
        town: null,
        county: null,
        postcode: null,
        country: null,
      },
      addAccount: {
        name: null,
        live: null,
        streaming: null,
        spotify: null,
        facebook: null,
        twitter: null,
      },
      spotifyAccountMethod: "",
      agreedTerms: false,
      songsTable: {
        page: 1,
        perPage: 10,
        sortBy: null,
        globalFilter: null,
      },
      range: {
        receiptDate: {
          format: "strict_date_optional_time",
          gte: from.toISOString(),
          lte: to.toISOString(),
        },
        publishedStatementDate: {
          format: "strict_date_optional_time",
          gte: from.toISOString(),
          lte: to.toISOString(),
        },
      },
      selectedDates: {
        fromDate: from.toISOString(),
        toDate: to.toISOString(),
      },
      dataRange: {
        from: null,
        to: null,
      },
      royaltiesFilter: [],
      navItemsEnabled: true,
      apiUrl: import.meta.env.VITE_API,
      hubspotDisabled: null,
      clientDisplayName: "",

      // Simple actions - just like AppState.update()
      updateState: updates => set(state => ({ ...state, ...updates })),

      // Specific actions for common operations
      updateAddAccount: updates => set(state => ({
        addAccount: { ...state.addAccount, ...updates },
      })),

      updateOnboarding: updates => set(state => ({
        onboarding: { ...state.onboarding, ...updates },
      })),

      updateSongsTable: updates => set(state => ({
        songsTable: { ...state.songsTable, ...updates },
      })),
    }),
    {
      name: "@userData", // Use same storage key as original
      storage: createJSONStorage(() => sessionStorage),

      // Handle migrations like your original code
      migrate: (persistedState) => { // Removed unused 'version' parameter
        const state = persistedState;

        // Apply the same migrations as your original code
        if (state.songsTable) {
          state.songsTable = {
            page: 1,
            perPage: 10,
            sortBy: [],
            globalFilter: null,
          };

          if (!state.songsTable.globalFilter) {
            state.songsTable.globalFilter = null;
          }

          if (!state.songsTable.sortBy) {
            state.songsTable.sortBy = [];
          }
        }

        if (!state.range) {
          const migrationTo = new Date();
          const migrationFrom = new Date(migrationTo);
          migrationFrom.setFullYear(migrationFrom.getFullYear() - 1);

          state.range = {
            receiptDate: {
              format: "strict_date_optional_time",
              gte: migrationFrom.toISOString(),
              lte: migrationTo.toISOString(),
            },
            publishedStatementDate: {
              format: "strict_date_optional_time",
              gte: migrationFrom.toISOString(),
              lte: migrationTo.toISOString(),
            },
          };
        }

        // Always update to use reasonable defaults instead of 2000-01-01
        const migrationTo = new Date();
        const migrationFrom = new Date(migrationTo);
        migrationFrom.setFullYear(migrationFrom.getFullYear() - 1);

        state.range = {
          receiptDate: {
            format: "strict_date_optional_time",
            gte: migrationFrom.toISOString(),
            lte: migrationTo.toISOString(),
          },
          publishedStatementDate: {
            format: "strict_date_optional_time",
            gte: migrationFrom.toISOString(),
            lte: migrationTo.toISOString(),
          },
        };

        if (!state.selectedDates) {
          state.selectedDates = {
            fromDate: migrationFrom.toISOString().split("T", 1)[0],
            toDate: migrationTo.toISOString().split("T", 1)[0],
          };
        }

        if (!state.dataRange) {
          state.dataRange = {
            from: null,
            to: null,
          };
        }

        if (!state.royaltiesFilter) {
          state.royaltiesFilter = [];
        }

        return state;
      },
    },
  ),
);

export default useAppStore;
